import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { Nullable } from 'common/models/optional';
import {
  ContextWithStringAmount,
  GeneralErrorEvent,
} from 'common/models/xstate';
import { RedeemApi } from 'sdk/redeemApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { InputUtils } from 'utils/inputUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, Machine } from 'xstate';
import { RedeemEvent } from './redeemEvent';
import { XStateUtils } from './xStateUtils';
import { ChainId } from 'constants/constants';
import { MintableToken } from 'constants/contracts';
import { Address } from 'viem';
import { SwalUtils } from 'utils/swalUtils';

export enum RedeemState {
  WAIT_FOR_INPUT = 'WAIT_FOR_INPUT',
  REDEEMING = 'REDEEMING',
  REDEEMING_SUCCESS = 'REDEEMING_SUCCESS',
  REDEEMING_ERROR = 'REDEEMING_ERROR',
  CHECK_INPUT = 'CHECK_INPUT',
  LOADING_DATA = 'LOADING_DATA',
  INVALID_INPUT = 'INVALID_INPUT',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
  INSUFFICIENT_ONE_TOKEN = 'INSUFFICIENT_ONE_TOKEN',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  RESETTING_ONE_TOKEN_ALLOWANCE = 'RESETTING_ONE_TOKEN_ALLOWANCE',
  APPROVING_ONE_TOKEN_ALLOWANCE = 'APPROVING_ONE_TOKEN_ALLOWANCE',
  CHECKING_FOR_EXISTING_TXS = 'CHECKING_FOR_EXISTING_TXS',
}

enum RedeemActions {
  REDEEM_ERROR = 'REDEEM_ERROR',
  RESET_ERROR = 'RESET_ERROR',
  APPROVE_ERROR = 'APPROVE_ERROR',
  GET_DATA_ERROR = 'GET_DATA_ERROR',
}

type RedeemSchema = {
  states: {
    [s in RedeemState]: object;
  };
};

export type EventInputChangeArgs = {
  inputValue: string;
  currentEthBalance: number;
};

export type EventUpdateAccountInfoArgs = {
  address: Address;
  chainId: number;
};

export type EventSelectTokenToRedeemArgs = {
  tokenContractContainer: MintableToken;
  redeemFee: number;
};

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventInputChange = {
  type: RedeemEvent.INPUT_CHANGE;
  value: EventInputChangeArgs;
};
type EventUpdateAccountInfo = {
  type: RedeemEvent.UPDATE_ACCOUNT_INFO;
  value: EventUpdateAccountInfoArgs;
};
type EventCheckInput = { type: RedeemEvent.CHECK_INPUT; value: void };
type EventRedeem = { type: RedeemEvent.REDEEM; value: void };
type EventSelectTokenToRedeem = {
  type: RedeemEvent.SELECT_TOKEN_TO_REDEEM;
  value: EventSelectTokenToRedeemArgs;
};
type EventResetMemberAllowance = {
  type: RedeemEvent.RESET_ONE_TOKEN_ALLOWANCE;
  value: void;
};
type EventApproveMemberAllowance = {
  type: RedeemEvent.APPROVE_ONE_TOKEN_ALLOWANCE;
  value: void;
};

type RedeemEvents =
  | EventInputChange
  | EventCheckInput
  | EventRedeem
  | EventSelectTokenToRedeem
  | EventResetMemberAllowance
  | EventApproveMemberAllowance
  | EventUpdateAccountInfo;

type RedeemContext = {
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  amount: string;
  redeemFee: number;
  gasLimit: bigint;
  tokenContractContainer: Nullable<MintableToken>;
  isRedeeming: Nullable<boolean>;
  redeemResponse: Nullable<ContractTxAsyncActionResponse>;
  redeemError: Nullable<Error>;
  redeemDataResponse: Nullable<RedeemApi.GetRedeemDataResponse>;
  isOneTokenApproved: Nullable<boolean>;
  isApprovingOneToken: boolean;
  isResettingOneToken: boolean;
  isLoadingData: boolean;
} & ContextWithStringAmount;

export const initialRedeemContext: RedeemContext = {
  accountAddress: null,
  chainId: null,
  amount: '',
  redeemFee: 0,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.redeem),
    ChainId.Mainnet
  ),
  tokenContractContainer: null,
  isRedeeming: false,
  redeemResponse: null,
  redeemError: null,
  redeemDataResponse: null,
  // Assume these are true so the UI will show Redeem button first
  // If resolved to false before redeeming then the approval buttons
  // will be shown
  isOneTokenApproved: null,
  isApprovingOneToken: false,
  isResettingOneToken: false,
  isLoadingData: false,
};

const isRedeemSuccessful = (context: RedeemContext) => {
  // If the message is present, then redeem was successful
  // return InputUtils.isTruthy(context.redeemResponse?.output.message);
  return InputUtils.isTruthy(context.redeemResponse?.output);
};

const isInsufficientOneToken = (context: RedeemContext) => {
  if (
    !context.redeemDataResponse ||
    !context.redeemDataResponse.collateralTokenBN
  ) {
    return true; // true being insufficient collateral
  }
  return !context.redeemDataResponse.isSufficientOneToken;
};

const redeemApi: RedeemApi.IRedeem = RedeemApi.OneTokenRedeem.getInstance();

type RedeemResponseEvent = {
  type: string;
  // Note this call will return immediately, we know it's still active
  // if the data.hash is defined
  // data: ContractTxAsyncActionResponse<RedeemApi.RedeemResponse>;
  data: ContractTxAsyncActionResponse;
};

type RedeemErrorEvent = {
  type: string;
  data: Error;
};
type GetRedeemDataResponseEvent = {
  type: string;
  data: RedeemApi.GetRedeemDataResponse;
};
type GetBooleanResponseEvent = {
  type: string;
  data: boolean;
};

// https://github.com/ichifarm/ichi-app/blob/master/src/pages/Redeem/index.tsx#L488
export const redeemMachine = Machine<RedeemContext, RedeemSchema, RedeemEvents>(
  {
    key: 'redeem',
    id: 'redeem',
    initial: RedeemState.WAIT_FOR_INPUT,
    context: XStateUtils.shortStringify(initialRedeemContext),
    states: {
      [RedeemState.WAIT_FOR_INPUT]: {
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (context, event) => event.value.inputValue,
            }),
          },
          // Once the user has a connected wallet then let's check for existing transactions
          [RedeemEvent.UPDATE_ACCOUNT_INFO]: {
            target: RedeemState.CHECK_INPUT,
            actions: assign({
              accountAddress: (context, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (context, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              tokenContractContainer: (
                context,
                event: EventSelectTokenToRedeem
              ) => event.value.tokenContractContainer,
              redeemFee: (context, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
          [RedeemEvent.REDEEM]: {
            target: RedeemState.REDEEMING,
          },
          [RedeemEvent.RESET_ONE_TOKEN_ALLOWANCE]: {
            target: RedeemState.RESETTING_ONE_TOKEN_ALLOWANCE,
          },
          [RedeemEvent.APPROVE_ONE_TOKEN_ALLOWANCE]: {
            target: RedeemState.APPROVING_ONE_TOKEN_ALLOWANCE,
          },
        },
      },
      // NOTE here I'm really assuming the only possible tx is minting, so if we want to support more types
      // in the future, would need to rework this some.
      [RedeemState.CHECKING_FOR_EXISTING_TXS]: {
        invoke: {
          src: (context) => {
            return TransactionUtils.hasTx({
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              functionName: RedeemApi.OneTokenRedeem.redeemFN,
            });
          },
          // Success means there is a mint tx, re-execute mint
          onDone: {
            target: RedeemState.REDEEMING,
          },
          // Error just means no tx found, go back to waiting for input
          onError: {
            target: RedeemState.CHECK_INPUT,
            actions: assign({
              isRedeeming: () => false,
            }),
          },
        },
      },
      [RedeemState.REDEEMING]: {
        entry: assign({
          isRedeeming: () => true,
        }),
        invoke: {
          src: (context) => {
            return redeemApi.redeem({
              chainId: context.chainId as number,
              accountAddress: context.accountAddress as string,
              amount: context.amount,
              contractContainer:
                context.tokenContractContainer as MintableToken,
              redeemFee: context.redeemFee,
            });
          },
          onDone: {
            // Here we aren't going to indicate success as this is 'detached' async response where
            // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
            // See the activities below
            target: RedeemState.CHECKING_FOR_EXISTING_TXS,
            actions: assign({
              redeemResponse: (_, event: RedeemResponseEvent) => event.data,
              isRedeeming: (_, event: RedeemResponseEvent) =>
                InputUtils.isTruthy(event.data.hash), // if the hash is defined, we are minting
              // if success zero out the amount
              amount: (context, event: RedeemResponseEvent) =>
                !InputUtils.isTruthy(event.data.hash) ? '' : context.amount,
            }),
          },
          onError: {
            target: RedeemState.REDEEMING_ERROR,
            actions: [
              RedeemActions.REDEEM_ERROR,
              assign({
                redeemError: (_, event: RedeemErrorEvent) => event.data,
                isRedeeming: () => false,
              }),
            ],
          },
        },
      },
      [RedeemState.RESETTING_ONE_TOKEN_ALLOWANCE]: {
        entry: assign({
          isResettingOneToken: () => true,
        }),
        exit: assign({
          isResettingOneToken: () => false,
        }),
        invoke: {
          src: (context) => {
            return redeemApi.resetOneTokenAllowance({
              accountAddress: context.accountAddress as string,
              contractContainer:
                context.tokenContractContainer as MintableToken,
              chainId: context.chainId as number,
            });
          },
          onDone: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              isOneTokenApproved: () => false, // true means successfully reset, so if true the member is not approved
              // When executed reset amount to '' to force user input to re-enter value thus providing the needed
              // delay for the contract call to check allowance to succeed
              amount: () => '',
            }),
          },
          onError: {
            target: RedeemState.LOADING_DATA,
            actions: [
              RedeemActions.RESET_ERROR,
              assign({
                isOneTokenApproved: () => false, // true means successfully reset, so if true the member is not approved
              }),
            ],
          },
        },
      },
      [RedeemState.APPROVING_ONE_TOKEN_ALLOWANCE]: {
        entry: assign({
          isApprovingOneToken: () => true,
        }),
        exit: assign({
          isApprovingOneToken: () => false,
        }),
        invoke: {
          src: (context) => {
            return redeemApi.approveOneTokenAllowance({
              accountAddress: context.accountAddress as string,
              contractContainer:
                context.tokenContractContainer as MintableToken,
              chainId: context.chainId as number,
            });
          },
          onDone: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              isOneTokenApproved: (_, event: GetBooleanResponseEvent) =>
                event.data != null, // true means successfully approved
            }),
          },
          onError: {
            target: RedeemState.LOADING_DATA,
            actions: [
              RedeemActions.APPROVE_ERROR,
              assign({
                isOneTokenApproved: () => false, // false means not approved
              }),
            ],
          },
        },
      },
      [RedeemState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        invoke: {
          src: (context) => {
            return redeemApi.getRedeemData({
              accountAddress: context.accountAddress as string,
              contractContainer:
                context.tokenContractContainer as MintableToken,
              chainId: context.chainId as number,
              amount: context.amount,
              redeemFee: context.redeemFee,
            });
          },
          onDone: {
            target: RedeemState.CHECKING_FOR_EXISTING_TXS,
            actions: assign({
              redeemDataResponse: (_, event: GetRedeemDataResponseEvent) =>
                event.data,
              isOneTokenApproved: (_, event: GetRedeemDataResponseEvent) =>
                event.data.isOneTokenApproved,
              redeemFee: (_, event: GetRedeemDataResponseEvent) =>
                event.data.redeemFee,
              isLoadingData: () => false,
            }),
          },
          onError: {
            // TODO: Likely we should have another state to indicate to the user that there was an error fetching the metadata
            target: RedeemState.CHECK_INPUT,
            actions: [
              RedeemActions.GET_DATA_ERROR,
              assign({
                isLoadingData: () => false,
              }),
            ],
          },
        },
      },
      [RedeemState.CHECK_INPUT]: {
        always: [
          {
            target: RedeemState.REDEEMING_SUCCESS,
            cond: isRedeemSuccessful,
          },
          // {
          //   target: RedeemState.CHECKING_FOR_EXISTING_TXS,
          //   cond: canCheckExistingTxs,
          // },
          {
            target: RedeemState.WAIT_FOR_INPUT,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: RedeemState.WAIT_FOR_INPUT,
            cond: InputUtils.isZeroInput,
          },
          {
            target: RedeemState.NO_WALLET_CONNECTED,
            cond: InputUtils.isNullChainId,
          },
          {
            target: RedeemState.INSUFFICIENT_ONE_TOKEN,
            cond: isInsufficientOneToken,
          },
          {
            target: RedeemState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals18,
          },
          {
            target: RedeemState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: RedeemState.WAIT_FOR_INPUT,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [RedeemState.INVALID_INPUT]: {
        meta: {
          message: 'Invalid Input!',
        },
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (context, event) => event.value.inputValue,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
        },
      },
      [RedeemState.INSUFFICIENT_ONE_TOKEN]: {
        meta: {
          message: 'Insufficient Collateral amount!',
        },
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
        },
      },
      [RedeemState.TOO_MANY_DECIMALS]: {
        meta: {
          message: 'Too many decimals!',
        },
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
        },
      },
      [RedeemState.NO_WALLET_CONNECTED]: {
        meta: {
          message: 'Please connect a wallet!',
        },
        // entry: assign({
        //   inputError: () => 'Please connect a wallet!'
        // }),
        // exit: assign({
        //   inputError: () => undefined
        // }),
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
          [RedeemEvent.UPDATE_ACCOUNT_INFO]: {
            target: RedeemState.CHECK_INPUT,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [RedeemState.REDEEMING_SUCCESS]: {
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
              redeemResponse: () => null,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              redeemResponse: () => null,
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
        },
      },
      [RedeemState.REDEEMING_ERROR]: {
        meta: {
          message: 'Error minting!',
        },
        on: {
          [RedeemEvent.INPUT_CHANGE]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              redeemError: () => null,
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [RedeemEvent.SELECT_TOKEN_TO_REDEEM]: {
            target: RedeemState.LOADING_DATA,
            actions: assign({
              redeemError: () => null,
              tokenContractContainer: (_, event: EventSelectTokenToRedeem) =>
                event.value.tokenContractContainer,
              redeemFee: (_, event: EventSelectTokenToRedeem) =>
                event.value.redeemFee,
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      [RedeemActions.REDEEM_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        console.log('ERROR HERE!!!');
        console.log(event);
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Redeeming!',
        });
      },
      [RedeemActions.RESET_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        console.log('ERROR HERE!!!');
        console.log(event);
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Resetting!',
        });
      },
      [RedeemActions.APPROVE_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        console.log('ERROR HERE!!!');
        console.log(event);
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Approving!',
        });
      },
      [RedeemActions.GET_DATA_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        console.log('ERROR HERE!!!');
        console.log(event);
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ??
            evt?.data?.message ??
            'Error Getting Data!',
        });
      },
    },
  }
);
